// ScaledTextWrapper.tsx (puedes colocarlo en el mismo archivo WarmUp o importarlo)
import React, { useEffect, useRef } from 'react';

interface Props {
  scale?: number; // ej: 2 => doble tamaño
  children: React.ReactNode;
}

const ScaledTextWrapper: React.FC<Props> = ({ scale = 2, children }) => {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    const els = Array.from(root.querySelectorAll<HTMLElement>('*'));

    // incluir el propio wrapper también
    els.unshift(root);

    // Guardamos originales para restaurar después
    const originals: Array<{ el: HTMLElement; font?: string; line?: string }> = [];

    els.forEach((el) => {
      const cs = window.getComputedStyle(el);
      const font = cs.fontSize; // e.g. "16px" or "1rem"
      const line = cs.lineHeight; // could be "normal" or "24px" etc

      // sólo trabajamos cuando computed fontSize está en px (getComputedStyle siempre devuelve px)
      // getComputedStyle devuelve px, así que isPx check será true. Lo dejamos por claridad.
      if (font) {
        const parsed = parseFloat(font);
        if (!Number.isNaN(parsed) && parsed > 0) {
          // guardar original
          originals.push({ el, font: el.style.fontSize, line: el.style.lineHeight });

          // aplicar nuevo font-size en px
          el.style.fontSize = `${Math.round(parsed * scale)}px`;

          // line-height: mantenemos proporción más natural
          // En lugar de escalar 1:1, usamos un factor reducido para mejor legibilidad
          if (line && line !== 'normal') {
            const parsedLine = parseFloat(line);
            if (!Number.isNaN(parsedLine) && parsedLine > 0) {
              // Factor dinámico: para pantallas altas (scale >= 2.0) usar factor más compacto
              // Para scales normales (1.2) mantener el factor original 0.6
              let lineFactor;
              if (scale >= 2.0) {
                lineFactor = 0.3; // Pantallas 2K+: line-height muy compacto
              } else if (scale >= 1.5) {
                lineFactor = 0.45; // Pantallas intermedias: factor intermedio
              } else {
                lineFactor = 0.6; // Pantallas normales: factor original que funciona bien
              }

              const lineScale = 1 + (scale - 1) * lineFactor;
              el.style.lineHeight = `${Math.round(parsedLine * lineScale)}px`;
            }
          }
        }
      }
    });

    // cleanup: restaurar inline styles
    return () => {
      originals.forEach(({ el, font, line }) => {
        if (font !== undefined) el.style.fontSize = font;
        if (line !== undefined) el.style.lineHeight = line;
      });
    };
  }, [scale]);

  return (
    <div ref={ref} className="w-full h-full">
      {children}
    </div>
  );
};

export default ScaledTextWrapper;
